/**
 * HeadLight environment and host rules.
 *
 * Every deployed app answers on `<subdomain>[.<env>].headlight.com` (production has no env label).
 * This is the one place that turns an environment into an app's base URL, and a hostname back into
 * an environment, so labels, links and redirects agree.
 */

const HEADLIGHT_DOMAIN = 'headlight.com';

/** Domains whose `links.` host is still honoured when decoding old labels. */
const LEGACY_DOMAINS = [ 'paviasystems.com' ];

/** @type {Record<string, string>} environment name → hostname label ('' for production) */
const ENVIRONMENT_LABELS =
{
	Production: '',
	QA: 'qa',
	Staging: 'stg',
	Demo: 'demo',
	UAT: 'uat',
};

/** Alternate spellings seen in the wild (iOS uses `stage`). */
const LABEL_ALIASES =
{
	stage: 'stg',
};

/**
 * @typedef {Object} ParsedHost
 * @property {string} Subdomain - First label, e.g. `links`, `lims`.
 * @property {string} Environment - One of the ENVIRONMENT_LABELS keys, or '' when not a known HeadLight host.
 * @property {string} Domain - `headlight.com`, a legacy domain, or ''.
 * @property {boolean} IsHeadlight - True when the host is a HeadLight (or legacy) host.
 */

/**
 * @param {string} pLabel
 * @return {string} environment name for a hostname label, or '' if unknown
 */
function getEnvironmentForLabel(pLabel)
{
	const tmpLabel = LABEL_ALIASES[pLabel] || pLabel;
	for (const [ tmpName, tmpEnvLabel ] of Object.entries(ENVIRONMENT_LABELS))
	{
		if (tmpEnvLabel === tmpLabel)
		{
			return tmpName;
		}
	}
	return '';
}

/**
 * Parse a hostname into subdomain / environment / domain.
 *
 * @param {string} pHostname - e.g. `links.qa.headlight.com` (a port is ignored)
 * @return {ParsedHost}
 */
function parseHostname(pHostname)
{
	const tmpResult = { Subdomain: '', Environment: '', Domain: '', IsHeadlight: false };
	if (typeof pHostname !== 'string' || !pHostname)
	{
		return tmpResult;
	}
	const tmpHost = pHostname.toLowerCase().split(':')[0];
	const tmpDomains = [ HEADLIGHT_DOMAIN ].concat(LEGACY_DOMAINS);
	for (const tmpDomain of tmpDomains)
	{
		if (!tmpHost.endsWith(`.${tmpDomain}`))
		{
			continue;
		}
		const tmpLabels = tmpHost.slice(0, -(tmpDomain.length + 1)).split('.');
		if (tmpLabels.length === 1)
		{
			return { Subdomain: tmpLabels[0], Environment: 'Production', Domain: tmpDomain, IsHeadlight: true };
		}
		if (tmpLabels.length === 2)
		{
			const tmpEnvironment = getEnvironmentForLabel(tmpLabels[1]);
			if (tmpEnvironment)
			{
				return { Subdomain: tmpLabels[0], Environment: tmpEnvironment, Domain: tmpDomain, IsHeadlight: true };
			}
		}
		return tmpResult;
	}
	return tmpResult;
}

/**
 * True for hosts that only exist on the developer's machine: `localhost`, any `*.localhost` subdomain (browsers
 * resolve those to loopback; the QA persona proxy serves each persona at `<IDUser>.localhost:<port>`), and loopback
 * addresses.
 *
 * @param {string} pHostname - e.g. `21952.localhost:47203` (a port is ignored)
 * @return {boolean}
 */
function isLocalHostname(pHostname)
{
	const tmpRaw = String(pHostname || '').toLowerCase();
	if (tmpRaw === '::1' || tmpRaw.startsWith('[::1]'))
	{
		return true;
	}
	const tmpHost = tmpRaw.split(':')[0];
	return (tmpHost === 'localhost') || tmpHost.endsWith('.localhost') || tmpHost.startsWith('127.');
}

/**
 * The environment a browser page or server request is running in.
 * Local hosts (see isLocalHostname) map to QA, matching what local development talks to.
 *
 * @param {string} pHostname
 * @return {string} environment name ('Production' when unknown)
 */
function getEnvironmentFromHostname(pHostname)
{
	if (isLocalHostname(pHostname))
	{
		return 'QA';
	}
	const tmpHost = String(pHostname || '').toLowerCase().split(':')[0];
	const tmpParsed = parseHostname(tmpHost);
	return tmpParsed.Environment || 'Production';
}

/**
 * The environment a page is running in. A page opened from a file (the iOS app's offline forms) has no
 * host, so the API server the app injected decides instead.
 *
 * @param {{ Protocol?: string, Hostname?: string, ServerURL?: string }} pPage - e.g. from `window.location`
 *   and `window._OfflineServerBaseURL`
 * @return {string} environment name ('Production' when unknown)
 */
function getEnvironmentForPage(pPage)
{
	const tmpPage = pPage || {};
	if ((tmpPage.Protocol === 'file:' || !tmpPage.Hostname) && tmpPage.ServerURL)
	{
		try
		{
			return getEnvironmentFromHostname(new URL(String(tmpPage.ServerURL)).hostname);
		}
		catch (pError)
		{
			return 'Production';
		}
	}
	return getEnvironmentFromHostname(tmpPage.Hostname);
}

/**
 * Base URL for an app in an environment, with a trailing slash.
 *
 * @param {string} pSubdomain - e.g. `lims`, `links`
 * @param {string} [pEnvironment] - environment name; defaults to Production
 * @return {string}
 */
function getAppBaseURL(pSubdomain, pEnvironment)
{
	const tmpLabel = ENVIRONMENT_LABELS[pEnvironment || 'Production'];
	const tmpEnvSegment = tmpLabel ? `.${tmpLabel}` : '';
	return `https://${pSubdomain}${tmpEnvSegment}.${HEADLIGHT_DOMAIN}/`;
}

/**
 * Base URL of the links app (no trailing slash), which every printed label and emailed link targets.
 *
 * @param {string} [pEnvironment]
 * @return {string}
 */
function getLinksBaseURL(pEnvironment)
{
	return getAppBaseURL('links', pEnvironment).slice(0, -1);
}

/**
 * @param {string} pHostname
 * @return {boolean} true for `links[.<env>].headlight.com` and the legacy links hosts
 */
function isLinksHostname(pHostname)
{
	const tmpParsed = parseHostname(pHostname);
	return tmpParsed.IsHeadlight && tmpParsed.Subdomain === 'links';
}

module.exports =
{
	HEADLIGHT_DOMAIN,
	LEGACY_DOMAINS,
	ENVIRONMENT_LABELS,
	parseHostname,
	isLocalHostname,
	getEnvironmentFromHostname,
	getEnvironmentForPage,
	getAppBaseURL,
	getLinksBaseURL,
	isLinksHostname,
};
